export default {
  app: {
    title: 'Scribe',
    description: 'Automatic subtitles and transcriptions',
  },
  title: 'Subtitles',
  loadingText: 'Loading subtitles...',
  createButton: 'Create',
  newButton: 'New',
  viewGrid: 'Grid view',
  viewList: 'List view',
  list: {
    name: 'Name',
    subtitles: 'Subtitles',
    review: 'Check',
    duration: 'Duration',
    date: 'Date',
    actions: 'Actions',
  },
  comingSoon: {
    title: 'Coming soon',
    description: 'This feature is under development.',
  },
  error: {
    title: 'Failed to load subtitles',
    description: 'An error occurred while loading your subtitles. Please try again.',
    retryButton: 'Retry',
  },
  generation: {
    started: 'Subtitle generation started',
    error: 'Failed to start subtitle generation',
    partialError: '{failed} language(s) failed to start',
    inProgress: 'Generating…',
    failed: 'Generation failed',
    retry: 'Retry generation',
    retryStarted: 'Generation restarted',
    retryError: 'Could not restart generation',
    // A transcription that failed in the background: the list's card and the toast.
    transcriptionFailed: {
      title: 'Transcription failed',
      hint: 'The transcription did not complete. You can relaunch it.',
      toast: 'The transcription failed',
      toastNamed: 'The transcription of “{name}” failed',
      retry: 'Relaunch',
      dismiss: 'Dismiss',
    },
    // A transcription that heard nothing: the wizard, the list's card and the toast.
    noSpeech: {
      title: 'No speech detected',
      description: 'This video contains no speech, so automatic subtitles cannot be generated. You can import a subtitle file instead.',
      short: 'No speech',
      hint: 'Automatic subtitles cannot be generated.',
      toast: 'No speech detected: the subtitles could not be generated',
      toastNamed: 'No speech detected in “{name}”',
      see: 'View',
      import: 'Import an .srt/.vtt file',
      dismiss: 'Dismiss',
    },
  },
  filter: {
    searchPlaceholder: 'Search a project…',
    review: {
      all: 'All projects',
      internalGroup: 'Your check',
      todo: 'To check',
      wip: 'Check in progress',
      done: 'Checked',
      externalGroup: 'External review',
      extSent: 'Sent for review',
      extDone: 'Reviewed externally',
    },
    noResults: {
      title: 'No projects match your filters',
      description: 'Try adjusting your search or filters.',
      clear: 'Clear filters',
    },
    types: {
      video: 'Video',
      audio: 'Audio',
      imported: 'Imported',
    },
  },
  assetType: {
    video: 'Video',
    audio: 'Audio',
    subtitles: 'Imported',
  },
  emptyState: {
    title: 'Start subtitling your videos',
    description: 'This feature allows you to generate subtitles and transcriptions for your videos. Click "Start" to begin.',
    buttonText: 'Start',
  },
  header: {
    title: 'Subtitle',
    titleAccent: 'editor',
    subtitleEmpty: 'Transcribe, translate and edit subtitles for your videos — in over 100 languages.',
    subtitleFilled: 'No projects | 1 project | {total} projects',
    hint: 'Your recent subtitling projects. All subtitles also remain accessible directly from each video in the library.',
    newProject: 'New',
  },
  empty: {
    heroTitle: 'Your first subtitling project',
    heroDescription: 'Pick a video from your library or upload a new one. We auto-detect the language and prepare a transcription for you.',
    fromLibrary: 'Pick from my library',
    uploadFile: 'Upload a video',
    demoTitle: 'Watch the presentation',
    demoSubtitle: 'Discover Scribe in video',
    howItWorksTitle: 'How it works',
    languagesTitle: 'Over 100 languages',
    languagesSubtitle: 'Automatic transcription and translation · 98% accuracy',
    steps: {
      import: {
        title: 'Import your video',
        description: 'MP4, MOV, WebM — up to 2 GB',
      },
      transcribe: {
        title: 'Auto transcription',
        description: 'In over 100 languages, with word-level timing',
      },
      edit: {
        title: 'Edit & style',
        description: 'Fix, segment, customize your subtitles',
      },
      export: {
        title: 'Export',
        description: 'SRT, VTT, or MP4 video with burn-in',
      },
    },
  },
  card: {
    created: 'Created on',
    downloadLanguage: 'Download ({language})',
    subtitlesOnly: 'Subtitles only',
    linkVideo: 'Link video',
    noVideo: 'No video attached',
    noReadySubtitles: 'No subtitles ready yet',
  },
  nav: {
    glossary: 'Glossary',
    vocabulary: 'Vocabulary',
    sync: 'Sync Options',
    style: 'Default Style',
  },
  editor: {
    linkError: 'The video could not be linked to these subtitles.',
    styleSaveError: 'The style was not saved. Your changes stay on screen and will be sent again with the next edit.',
    title: 'Subtitle Editor',
    undo: 'Undo',
    redo: 'Redo',
    saved: 'Saved',
    saving: 'Saving...',
    unsaved: 'Unsaved changes',
    saveTooltip: 'Save changes (Ctrl+S)',
    allChangesSaved: 'All changes saved',
    loading: 'Loading...',
    welcome: {
      close: 'Close',
      title: 'Welcome — your subtitles are here',
      subtitle: '“{name}” and your edits have joined your workspace.',
      defaultFile: 'Your video',
      journey: {
        from: 'Express subtitling',
        to: 'Your Scribe space',
        moved: 'we brought everything back for you',
      },
      destsTitle: 'Where to find your work:',
      dest: {
        mediaHint: 'Your video and its subtitles',
        editor: 'Scribe editor',
        editorHint: 'Resume, style, export',
        translate: 'Translate',
        translateHint: 'Reach an international audience',
      },
      badge: {
        here: '1 file',
        languages: '100+ languages',
      },
      tip: {
        title: 'Your exports, whenever you want',
        text: 'Download in SRT, VTT, TXT or DOCX from the editor, as many times as you need.',
      },
      actions: {
        openEditor: 'Continue editing',
        newFile: 'Subtitle another file',
      },
    },
    unsavedChanges: 'Unsaved changes',
    unsavedChangesMessage: 'You have unsaved changes. Do you want to save them before leaving?',
    autoSaveError: 'Auto-save failed. Click the cloud icon to retry.',
    saveError: 'Save failed - Click to retry',
    openInMediasbox: 'Open in Mediasbox',
    breadcrumb: {
      label: 'Breadcrumb',
      scribe: 'Scribe',
      backToScribe: 'Back to Scribe',
      openAsset: 'View media',
    },
    autoGenerated: 'Auto-generated subtitle',
    manual: 'Manually created subtitle',
    auto: 'Auto',
    manualShort: 'Manual',
    noSegments: 'No subtitles yet',
    noSegmentsHint: 'Add your first subtitle to get started',
    addFirstSegment: 'Add first subtitle',
    tabs: {
      subtitles: 'Subtitles',
      transcription: 'Transcription',
      languages: 'Languages',
      styles: 'Style',
      search: 'Search',
      speakers: 'Speakers',
    },
  },
  review: {
    reviewed: 'checked',
    reviewedTooltip:
      'The segments you ticked ✓ in the editor. It tracks your own progress: it blocks neither the export nor publishing. To have someone else review, use “Share for review”.',
    checkSegment: 'Mark as checked',
    uncheckSegment: 'Mark as not checked',
    checkAll: 'Check all',
    uncheckAll: 'Uncheck all',
    checkAllTitle: 'Mark everything as checked?',
    checkAllText: '{n} segment(s) will be marked as checked. You can undo with Ctrl+Z.',
    uncheckAllTitle: 'Uncheck everything?',
    uncheckAllText: 'All {n} segments will go back to “not checked”. You can undo with Ctrl+Z.',
    uncertain: 'Scribe unsure',
    markAllPreviousAsReviewed: 'Check up to here',
    hideReviewed: 'Show all segments',
    showOnlyUnreviewed: 'Show only unchecked',
    allChecked: 'Everything is checked',
    allCheckedHint: 'The filter shows only unchecked segments, and none are left on this track.',
    progress: '{reviewed}/{total} segments checked',
    complete: 'Reviewed',
    none: 'Not reviewed',
    inProgress: 'Review in progress',
    autoGenerated: 'Auto-generated',
    share: {
      button: 'Share for review',
      buttonShort: 'Share',
      title: 'Share for review',
      subtitle: 'The reviewer corrects your subtitles directly in Scribe. Their corrections come back to you as suggestions — no file to exchange.',
      thisMedia: 'This media',
      autoSubtitles: 'Auto subtitles',
      languages: 'Languages to review',
      who: 'Who reviews?',
      byEmail: 'Invite by email',
      byLink: 'Share link',
      emailPlaceholder: "reviewer{'@'}example.com",
      linkNote: 'A link you can share with anyone.',
      returnMode: 'How corrections come back',
      suggestionTitle: 'As suggestions to review',
      suggestionDesc: 'You accept or ignore each correction. Recommended when you don’t speak the language.',
      directTitle: 'Applied directly',
      directDesc: 'Corrections are applied to the track immediately. Faster, less control.',
      expiration: 'Link expires',
      expire14: 'In 14 days',
      expire7: 'In 7 days',
      expire30: 'In 30 days',
      expire90: 'In 90 days',
      noPublicUrl: 'This deployment names no public address: pass on the token the response carried.',
      message: 'Message (optional)',
      messagePlaceholder: 'Hi, could you check the translation? Thanks!',
      preview: 'The reviewer opens Scribe without an account, guided to the passages the AI was unsure about.',
      submitEmail: 'Send invitation',
      submitLink: 'Create link',
      createError: 'Could not create the review link. Please try again.',
      doneSubtitle: 'Copy the link below and send it to your reviewer.',
      emailSentSubtitle: 'The review link has been emailed to {email}.',
      copyButton: 'Copy',
      copied: 'Copied',
      doneButton: 'Done',
      linkHint: 'The reviewer opens Scribe in review mode, without an account.',
      noSubtitle: 'Select a subtitle track first.',
      close: 'Close',
    },
    reviewer: {
      banner: 'You are reviewing the subtitles of “{media}”.',
      languagesToReview: '{count} languages to review',
      submit: 'Send review',
      confirmTitle: 'Send your review?',
      confirmMultiIntro: 'You are about to send your review for these languages:',
      confirmHint: 'Your corrections will be sent to the owner for review.',
      confirmContinue: 'Keep reviewing',
      loading: 'Loading the review…',
      submittedTitle: 'Thank you!',
      submittedMessage: 'Your corrections have been sent. The owner will review and apply them.',
      notFoundTitle: 'Review not available',
      notFoundMessage: 'This review link is invalid, has expired, or has been revoked.',
      errorTitle: 'Something went wrong',
      errorMessage: 'The review could not be loaded. Please check your link and try again.',
      submitError: 'Your review could not be sent. Please try again.',
      promoTitle: 'Subtitle your own videos',
      promoText: 'Scribe generates subtitles automatically, translates them into 100+ languages, and lets you fine-tune everything — no install.',
      promoCta: 'Try Scribe free',
      welcomeTitle: 'Review these subtitles',
      welcomeSubtitle: 'You’ve been asked to review “{media}”.',
      welcomeStep1: 'Listen to',
      welcomeStep1Desc: 'the video, line by line.',
      welcomeStep2: 'Fix',
      welcomeStep2Desc: 'the text directly if something is wrong.',
      welcomeStep3: 'Send',
      welcomeStep3Desc: 'your review: the owner gets your corrections.',
      startButton: 'Start reviewing',
      welcomeFoot: 'No account needed · it only takes a few minutes.',
      listen: 'Listen to this line',
      handledCount: 'No line reviewed | 1 line reviewed | {n} lines reviewed',
      validatedCount: '{done}/{total} validated',
      affirm: 'Looks right',
      unaffirm: 'Undo',
      affirmed: 'Validated',
      alreadySent: 'Review sent',
      propose: 'Propose',
      updateProposal: 'Update',
      proposed: 'Proposed',
      withdraw: 'Withdraw',
      original: 'Current line',
      addComment: 'Add a comment',
      commentPlaceholder: 'What you cannot fix yourself: a line to split, to merge…',
      startLabel: 'Line start',
      endLabel: 'Line end',
      movedTitle: 'This line has changed',
      movedHint: 'The owner edited it while you were reviewing, so your correction was not saved. Compare it with the new text, then propose it again.',
      movedNow: 'It now reads',
      takeNewText: 'Start from the new text',
      proposeError: 'Your correction could not be saved. Please try again.',
      noMedia: 'The media is not available here. Review the lines as they are.',
      noLines: 'This track holds no line.',
      onlyProposed: 'Lines I have been through ({n})',
      noneProposed: 'You have not been through any line of this track yet.',
      onlyUncertain: 'Scribe unsure ({n})',
      noneUncertain: 'Nothing left waiting: you have been through them all.',
      uncertainTip: 'Lines in orange are the ones Scribe was unsure about. Check them first.',
      modeSuggest: 'The owner will accept or dismiss each correction, one by one.',
      modeDirect: 'Your corrections are applied as soon as you send them, except those on a line edited in the meantime.',
      confirmHintDirect: 'Your corrections will be applied to the subtitles as soon as you send them.',
    },
    owner: {
      title: 'Proposed corrections',
      from: 'Reviewed by {who}',
      aReviewer: 'a reviewer',
      noChanges: 'No correction proposed — the reviewer changed nothing.',
      noneThisTrack: 'No correction on this track.',
      count: '{n} line(s) reviewed',
      affirmed: 'Validated as it stands',
      applied: 'Corrections applied.',
      partiallyApplied: '{n} correction(s) could not be applied: the line moved or is gone.',
      applyError: 'The corrections could not be applied. Please try again.',
      applyN: 'Apply {accepted} · dismiss {rejected}',
      closeAfter: 'Close the review',
      base: 'Before',
      live: 'Now',
      proposed: 'Proposed',
      lineGone: 'The line is no longer in the track.',
      refusedHint: '{n} correction(s) were not applied and are still waiting.',
      forbiddenHint:
        'Applying a correction rewrites the captions, which takes the right to edit subtitles in the workspace this media sits in. You can still read what the reviewer proposed.',
      notDeciding: {
        open: 'The reviewer has not sent their corrections yet: you can read them, not apply them yet.',
        closed: 'This review is closed: its corrections can be read, no longer decided.',
        revoked: 'This review’s link was revoked: its corrections can be read, no longer decided.',
      },
      state: {
        applicable: 'To decide',
        applied: 'Already applied',
        conflict: 'Line changed',
        orphaned: 'Line gone',
      },
      decision: {
        accepted: 'Accepted',
        rejected: 'Dismissed',
        pending: 'Waiting',
      },
      cannotAccept: {
        conflict: 'The line has changed since: carry the correction over by hand, then dismiss it.',
        orphaned: 'The line is gone: carry the correction over by hand, then dismiss it.',
        applied: 'Already applied.',
        applicable: '',
        comment: 'A comment alone: there is nothing to write onto the line.',
      },
    },
    inlineOwner: {
      languages: 'Languages',
      bannerFrom: '{who}’s corrections',
      bannerAnonymous: 'Reviewer corrections',
      proposals: '{n} to review',
      before: 'Before',
      after: 'After',
      accept: 'Accept',
      ignore: 'Ignore',
      allDecided: 'all decided',
      decidedSummary: '{accepted} accepted, {rejected} ignored',
      applyHint: 'Accept or ignore at least one correction',
      arbitrate: '{n} to settle',
      affirmed: 'The reviewer found this line right as it is',
      atReview: 'When reviewed',
      now: 'Now',
      timing: 'Timing',
      acceptAll: 'Accept all',
      apply: 'Apply ({n})',
      finish: 'Finish',
      noChangesShort: 'No changes',
      finished: 'Review completed',
      exit: 'Exit',
      confirmApplyTitle: 'Apply the corrections?',
      confirmApplyText: 'The accepted corrections will be applied to these {n} languages, then the review will be closed.',
      confirmCount: '{n} corrections',
      confirmNothing: 'Nothing to change',
      confirmApplyButton: 'Apply and finish',
      onlyChanges: 'Show only corrections',
      noneThisLanguage: 'No corrections in this language.',
      noProposals: 'No corrections — the reviewer kept everything as is.',
      applied: 'Corrections applied.',
      applyError: 'Could not apply the corrections. Please try again.',
    },
    list: {
      tabProjects: 'Projects',
      tabReviews: 'Reviews',
      empty: 'No reviews yet. Share a subtitle track to have it proofread.',
      groupToReview: 'To review',
      groupInProgress: 'In progress',
      groupDone: 'Done',
      viewCorrectionsN: 'View {n} corrections',
      trackCount: '1 track | {n} tracks',
      expiresOn: 'Expires on {date}',
      revoke: 'Revoke',
      revoked: 'Link revoked.',
      revokeError: 'The link could not be revoked. Please try again.',
      rotatedHint: 'A new link was created, and the previous one stopped working. Copy this one — it is shown once.',
      statusExpired: 'Expired',
      status: {
        open: 'Waiting for the reviewer',
        submitted: 'Corrections received',
        closed: 'Closed',
        revoked: 'Revoked',
      },
      sharedByLink: 'Share link',
      sentOn: 'Sent {date}',
      reviewedBadge: 'Reviewed',
      reviewedBadgeTooltip: 'An external review has been applied to this media.',
      resent: 'Invitation sent again to {email}.',
      resend: 'Resend link',
      resendError: 'Couldn’t send the invitation. Try again.',
      sendTitle: 'Resend the review link',
    },
  },
  segment: {
    add: 'Add',
    addBefore: 'Add before',
    split: 'Split',
    merge: 'Merge',
    deleteTitle: 'Delete segment',
    deleteMessage: 'This subtitle will be deleted. Are you sure you want to continue?',
  },
  translate: {
    translateButton: 'Translate',
    noTranslations: 'No translations in progress',
    startTranslation: 'Start translation',
    allLanguagesAlreadyExist: 'All selected languages already exist',
    translationsQueued: '{count} translation(s) started',
    translationCompleted: '{language} translation completed',
  },
  languages: {
    title: 'Languages',
    addLanguage: 'Add language',
    noLanguages: 'No languages available',
    noLanguagesHint: 'Add a language to start creating subtitles',
    default: 'Default',
    cues: '{count} segments',
    words: '{count} words',
    confidence: '{score}% confidence',
    generatedBy: 'Generated by {service}',
    translationsInProgress: 'Translations in progress',
    pending: 'Pending',
    failed: 'Failed',
    waitingInQueue: 'Waiting in queue...',
    generatingSubtitles: 'Generating subtitles...',
    generationCompleted: '{language} subtitles generated successfully',
    generationFailed: 'Subtitle generation failed',
    retry: 'Retry',
    retryStarted: 'Retrying {language}…',
    retryError: 'Could not restart the translation.',
    delete: {
      title: 'Delete language',
      message: 'Are you sure you want to delete the {language} subtitles? This action cannot be undone.',
      success: 'Language deleted successfully',
      error: 'Failed to delete language',
    },
  },
  // The cost of a generation, confirmed before it starts (`modals/SubtitlesCostConfirmModal.vue`)
  // and in the language modal's footer. The amounts are `billing.subtitlesCost`'s.
  costConfirm: {
    title: 'Generate subtitles',
    generate: 'Generate',
    generateFor: 'Generate · {amount}',
    after: 'Balance after the generation: {amount}.',
    short: 'Top up to start the generation: nothing is started or charged before.',
    shortReserve:
      'Your balance covers this price, but not what more would be charged if the video is in none of the languages chosen. Top up to start the generation: nothing is started or charged before.',
    neverFunded: 'You have not topped up yet: the credit you buy pays for subtitles, storage and delivery.',
    topUpTitle: 'Top up the wallet',
    topUpAndGenerate: 'Top up {amount} and generate',
    creditPending: 'Payment accepted. The credit can take a minute to arrive — the generation starts as soon as it does.',
    creditLate: 'Your payment went through, but the credit has not arrived yet. Try generating again in a moment.',
    refusedLabel: 'Generated subtitles',
  },
  addLanguage: {
    title: 'Add a language',
    subtitle: 'Select one or more languages to generate subtitles',
    labelOptional: 'Label (optional)',
    labelPlaceholder: 'e.g. French Subtitles',
    generate: 'Generate',
    generateMultiple: 'Generate {count} languages',
    generating: 'Starting generation...',
    selectLanguage: 'Select a language',
    noLanguagesAvailable: 'All languages already have subtitles',
    multipleSelected: '{count} languages selected',
    success: 'Subtitle generation started',
    successMultiple: '{count} subtitle generations started',
    error: 'Failed to start subtitle generation',
    noSpeechError:
      'No speech was detected in this media, so automatic subtitles cannot be generated. You can import a subtitle file instead.',
    waitingForTranscript: 'The media is being transcribed first; these languages will start as soon as it is done.',
    selected: 'Selected',
    popularLanguages: 'Popular languages',
    allLanguages: 'All languages',
    searchPlaceholder: 'Search a language...',
    noResults: 'No languages found',
  },
  noVideo: {
    title: 'No video linked',
    description: 'Link a video file to enable timeline editing and preview.',
    linkFromLibrary: 'Choose from library',
    uploadVideo: 'Upload video',
    tip: 'You can edit subtitles without a video, but linking one enables preview and precise synchronization.',
  },
  noAudio: {
    title: 'No audio linked',
    description: 'Link an audio file to enable timeline editing and preview.',
    linkFromLibrary: 'Choose from library',
    uploadAudio: 'Upload audio',
    tip: 'You can edit subtitles without an audio file, but linking one enables preview and precise synchronization.',
  },
  noMedia: {
    title: 'No media linked',
    description: 'Link a video or audio file to enable timeline editing and preview.',
    linkFromLibrary: 'Choose from library',
    tip: 'You can edit subtitles without media, but linking a video or audio enables preview and precise synchronization.',
  },
  speakers: {
    detectTitle: 'Identify the speakers',
    detectHint:
      'Scribe listens to the video and gives each line the voice that speaks most of it, without touching the text or the timing. You then name each voice once for the whole track.',
    detect: 'Identify the speakers',
    detectAgain: 'Run the detection again',
    queued: 'Detection queued…',
    running: 'Detecting the speakers…',
    failed: 'The detection could not finish. You can run it again.',
    noLines: 'The track needs lines before its speakers can be identified.',
    voices: 'Voices',
    loading: 'Loading…',
    noVoices: 'No voice yet: run a detection.',
    unavailable: 'No voice is assigned on this track.',
    speaker: 'Speaker {n}',
    add: 'Add a speaker',
    addPlaceholder: 'Name - role',
    addFailed: 'The speaker could not be added.',
    nameOf: 'Name of {name}',
    colorOf: 'Colour of {name}',
    lines: '{count} line | {count} line | {count} lines',
    landed: '{count} voice identified | {count} voice identified | {count} voices identified',
    alreadyRunning: 'A detection is already running on this track.',
    askFailed: 'The detection could not be started.',
    saveFailed: 'The voice could not be saved.',
    voiceOf: "Line's voice",
    nobody: 'No voice',
  },
  search: {
    searchPlaceholder: 'Search in subtitles...',
    caseSensitive: 'Case sensitive',
    wholeWord: 'Whole word',
    replacePlaceholder: 'Replace with...',
    replaceAll: 'Replace all',
    resultsCount: '{count} result(s)',
    replacedCount: '{count} result(s) ({replaced} replaced)',
    replaceTooltip: 'Replace in this segment',
    noResults: 'No results',
  },
  styles: {
    title: 'Subtitle Style',
    customize: 'Customize',
    resetToDefault: 'Default style',
    resetToDefaultHint: "Go back to the organization's style, set in Scribe's Settings › Style.",
    scopeNotice: 'This style applies to the video exported with burned-in subtitles. It does not apply to the Mediasbox player or the embeddable player, which display subtitles in their own style.',
    font: 'Font',
    fontFamily: 'Font family',
    fontWeight: 'Font weight',
    fontSize: 'Font size',
    italic: 'Italic',
    uppercase: 'Uppercase',
    colors: 'Colors',
    textColor: 'Text color',
    backgroundColor: 'Background color',
    backgroundOpacity: 'Background opacity',
    stroke: 'Stroke',
    strokeWidth: 'Stroke width',
    strokeColor: 'Stroke color',
    position: 'Position',
    verticalPosition: 'Vertical position',
    karaoke: 'Karaoke',
    enableKaraoke: 'Enable karaoke',
    currentWordColor: 'Current word color',
    karaokeNoTimings: 'Karaoke highlights each word as it is spoken, so it needs timing aligned to the audio. Only subtitles in the video\'s original spoken language have this — not translations into another language, nor imported files.',
    videoFormat: 'Video Format',
    selectFormat: 'Select format',
    categories: {
      all: 'All',
      social: 'Social',
      business: 'Business',
      retro: 'Retro',
    },
  },
  download: {
    title: 'Export',
    chooseType: 'What would you like to download?',
    subtitleFiles: 'Subtitle files',
    subtitleFilesDesc: 'Download your subtitles in SRT, VTT, ASS or other formats',
    videoWithSubtitles: 'Video with subtitles',
    videoWithSubtitlesDesc: 'Export your video with burned-in subtitles',
    back: 'Back',
    language: 'Language: {language}',
    format: 'Download format',
    selectFormat: 'Select Format',
    selectSubtitles: 'Select Subtitles to Download',
    selectTracks: 'Select the tracks to export',
    selectSubtitleLanguage: 'Select subtitle language to burn in',
    advancedOptions: 'Advanced Options',
    includeMetadata: 'Include metadata',
    zipMultiple: 'Download as ZIP archive',
    useSlugFilename: 'Use technical names for files',
    preview: 'Preview',
    downloadButton: 'Download',
    exportButton: 'Export',
    includeVideo: 'Include video with burned-in subtitles',
    videoFormat: 'Video format',
    videoFormatOriginal: 'Original format',
    videoFormatMP4: 'MP4',
    downloadingVideo: 'Downloading video with subtitles...',
    shareButton: 'Share',
    error: 'Failed to download subtitles',
    noSubtitles: 'No subtitles available to download',
    processing: 'Processing video...',
    encodingVideo: 'Encoding video with subtitles',
    preparingVideo: 'Preparing video file',
    uploadingResult: 'Uploading result',
    exportComplete: 'Export complete',
    exportFailed: 'Export failed',
    videoExport: {
      waitNotice:
        'Encoding can take a while depending on the video length. No need to wait around — you\'ll find the video in your transfers, bottom right, and by email if it\'s a long one.',
      progressTitle: 'Encoding your video…',
      phase: {
        preparing: 'Preparing',
        encoding: 'Encoding',
        finalizing: 'Finalizing',
      },
      step: {
        preparing: 'Preparing the video',
        encoding: 'Burning in the subtitles',
        finalizing: 'Finalizing and uploading',
      },
      phaseDesc: {
        preparing: 'We\'re preparing your video file and the styled subtitles.',
        encoding:
          'The subtitles are being burned into the picture, frame by frame. This is the longest step.',
        finalizing: 'We\'re making your video available to download.',
      },
      reassure:
        'You can close this window and keep working. The export carries on in your transfers, bottom right.',
      reassureEmail: 'Since it\'s a long video, we\'ll also email you at {email}.',
      reassureEmailNoAddress: 'Since it\'s a long video, we\'ll also email you when it\'s ready.',
      continueBackground: 'Continue in the background',
      completeTitle: 'Your video is ready',
      completeDesc: 'The download is starting. You\'ll also find it in your transfers.',
      downloadAgain: 'Download again',
      startError: 'Couldn\'t start the export. Please try again.',
      retry: 'Retry',
    },
  },
  export: {
    restricted: {
      all: "Your role does not allow exporting this video. Ask an administrator of the workspace.",
      files: "Your role does not allow exporting the subtitles or the transcript. Ask an administrator of the workspace.",
      burn: "Your role does not allow exporting the video with burned-in subtitles. Ask an administrator of the workspace.",
    },
    title: 'Export',
    subtitle: 'What would you like to export?',
    chooseType: 'What do you want to download?',
    subtitlesOnly: 'Subtitles',
    subtitleScript: 'Subtitle script',
    subtitleScriptDesc: 'In the selected format',
    subtitlesOnlyDesc: 'Download subtitle files in your preferred format',
    video: 'Video',
    videoWithSubtitles: 'Video with subtitles',
    videoWithSubtitlesDesc: 'An mp4 file with embedded subtitles',
    comingSoon: 'Coming soon',
    selectLanguage: 'Select language',
    selectLanguages: 'Select languages',
    format: 'Format',
    videoFormat: 'Video format',
    videoInfo: 'The video will be exported in MP4 format with burned-in subtitles',
    socialNetworks: 'Social Networks',
    currentLanguage: 'Current language',
    selectedFormat: 'Selected format',
    transcript: 'Transcript',
    transcriptDesc: 'The text by speaker, with timecodes: Word, Excel or TXT',
    columns: { timecode: 'Timecode', speaker: 'Speaker', text: 'Text' },
    exportFormat: 'Export format',
  },
  upload: {
    uploading: 'Uploading...',
    started: 'Upload started',
    error: 'Upload failed',
    plannedSubtitles: 'Subtitles to generate',
    status: {
      preparing: 'Preparing upload...',
      uploading: 'Uploading file...',
      processing: 'Processing video...',
    },
    // An import that failed and can still be resumed: its card, its toast and the processing modal.
    failed: {
      title: 'Import failed',
      hint: 'Resume it: the subtitles are asked for as soon as it lands.',
      retry: 'Resume the import',
      toastNamed: 'The import of “{name}” failed. {reason}',
      resumable: '{reason} Resume the import from the Transfers panel: the subtitles are asked for as soon as it lands.',
    },
    modal: {
      title: 'Upload Video',
      configure: 'Configure Subtitles',
      dragDrop: 'Drag and drop your video here',
      dropHere: 'Drop your file here',
      or: 'or',
      browse: 'Browse Files',
      fromLibrary: 'Choose from Library',
      uploadNew: 'Upload New Video',
      uploadNewDescription: 'Drag and drop or browse to upload a new video file',
      fromLibraryDescription: 'Select an existing video from your library',
      importSubtitle: 'Import SRT/VTT',
      importSubtitleDescription: 'Import an existing subtitle file',
      dragDropSubtitle: 'Drag and drop your SRT/VTT file here',
      dropSubtitleHere: 'Drop your subtitle file here',
      supportedSubtitleFormats: 'Supported formats: SRT, VTT',
      import: 'Import',
      errorInvalidSubtitleFormat: 'Invalid file format. Please use a .srt or .vtt file',
      errorParsingSubtitle: 'Error processing subtitle file',
      subtitleParsedSuccess: '{count} segments detected ({format})',
      chooseMethod: 'How would you like to add your video?',
      chooseMethodDescription: 'Select whether to upload a new video or choose from your existing library',
      tryDifferentFilter: 'Try a different filter or search term',
      supportedFormats: 'Supported formats: MP4, AVI, MOV, MKV, WebM',
      libraryTitle: 'Video Library',
      searchVideos: 'Search videos...',
      filterAll: 'All',
      filterNoSubtitles: 'No subtitles',
      filterRecent: 'Recent',
      noSubtitles: 'No subtitles yet',
      noVideosFound: 'No videos found',
      select: 'Select',
      existingVideo: 'Existing video',
      existingSubtitles: 'This video already has the following subtitles:',
      allowRegeneration: 'Allow regeneration of existing subtitles',
      selectLanguages: 'Select subtitle languages to generate',
      popular: 'Popular',
      presetPopular: 'Popular languages',
      presetEurope: 'European languages',
      presetAll: 'All languages',
      advancedOptions: 'Advanced Options',
      audioDescription: 'Audio Description',
      simplified: 'Simplified (Easy to read)',
      summary: '{count} language(s) selected',
      additionalSubtitles: 'Additional: {types}',
      startUpload: 'Start Upload',
      errorNotVideo: 'Please select a video file',
      errorTooLarge: 'File is too large (max 2GB)',
    },
  },
  delete: {
    title: 'Delete video',
    message: 'Are you sure you want to delete "{video}" and all its subtitles? This action cannot be undone.',
  },
  deleteSubtitles: {
    title: 'Delete subtitles',
    message: 'Are you sure you want to delete {count} subtitle(s) for "{video}"? This action cannot be undone.',
    success: 'Subtitles deleted successfully',
    error: 'Failed to delete subtitles',
  },
  transcription: {
    copy: 'Copy transcription',
    copied: 'Transcription copied',
    showSpeakers: 'Show speakers',
  },
  task: {
    upload: 'Uploading',
    transcription: 'Transcription',
    translation: 'Translation',
    status: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
    },
    retry: 'Retry',
    cancel: 'Cancel',
    remove: 'Remove',
    clearCompleted: 'Clear completed',
    noTasks: 'No tasks',
    tasksInProgress: '{count} task(s) in progress',
  },
  modal: {
    title: 'Generate Subtitles',
    chooseMethod: 'How would you like to add subtitles?',
    uploadNew: 'Upload Video/Audio',
    uploadNewDescription: 'Upload a new file and generate subtitles',
    fromLibrary: 'From Library',
    fromLibraryDescription: 'Select an existing file from your library',
    importSubtitle: 'Import Subtitles',
    importSubtitleDescription: 'Import an existing SRT or VTT file',
    dragDropVideo: 'Drag and drop your video or audio here',
    supportedVideoFormats: 'Supported formats: MP4, MOV, AVI, MKV, MP3, WAV, M4A',
    changeAsset: 'Change selection',
    selectAsset: 'Select a video or audio',
    selectAssetForSubtitle: 'Select the video/audio for this subtitle',
    selectVideoOrAudio: 'Select a video or audio file',
    dragDropSubtitle: 'Drag and drop your subtitle file here',
    supportedSubtitleFormats: 'Supported formats: SRT, VTT (max 10 MB)',
    fileTooLarge: 'File is too large (max {maxSize})',
    validating: 'Validating file...',
    words: 'Words',
    cues: 'cues',
    destination: 'Destination',
    workspace: 'Workspace',
    folder: 'Folder (optional)',
    folderPlaceholder: 'Search folder...',
    selectLanguages: 'Select subtitle languages to generate',
    advancedOptions: 'Advanced Options',
    audioDescription: 'Include audio descriptions',
    simplified: 'Simplified (Easy to read)',
    subtitleLanguage: 'Subtitle language',
    language: 'Language',
    labelOptional: 'Label (optional)',
    labelPlaceholder: 'e.g., French Subtitles',
    setAsDefault: 'Set as default subtitle',
    uploadAndGenerate: 'Upload & Generate',
    generateSubtitles: 'Generate Subtitles',
    uploadSubtitle: 'Upload Subtitle',
    linkToAsset: 'Link to video/audio',
    standalone: 'Standalone file',
    standaloneHint: 'The subtitle file will be uploaded as a standalone asset. You can link it to a video later.',
    error: 'Error',
    processing: 'Processing',
    complete: 'Complete',
    errorTitle: 'An error occurred',
    successTitle: 'Subtitles started',
    uploading: 'Uploading file...',
    importing: 'Importing subtitles...',
    startingGeneration: 'Starting generation...',
    successMessage: 'Subtitle generation has started. You can close this window.',
    uploadingMessage: 'Uploading... {progress}%',
    pleaseWait: 'Please wait...',
  },
  settings: {
    title: 'Settings',
    backToSubtitles: 'Back to subtitles',
    readOnly: "These are the organization's settings: your role can read them, not change them.",
    sections: {
      general: 'General',
    },
    glossary: {
      title: 'Glossary',
      description: "Your organization's proper names, brands, acronyms and technical terms, written as they should be. The AI respects them when it corrects or translates your subtitles.",
      add: 'Add',
      addTerm: 'Add a term',
      editTerm: 'Edit term',
      searchPlaceholder: 'Search terms…',
      noTerms: 'No terms in the glossary',
      noTermsHint: 'Add your proper names, brands and acronyms, or import them from a CSV file.',
      noResults: 'No term matches',
      noResultsHint: 'Try another search or remove the filters.',
      clearFilters: 'Remove filters',
      count: '{count} term | {count} terms',
      limit: '{count} terms are active: the AI reads only {max}, in alphabetical order. Deactivate the least useful ones.',
      saved: 'Term saved',
      saveError: 'The term was not saved.',
      toggleError: 'The term could not be turned on or off.',
      filters: {
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
      },
      uses: {
        title: 'Where the glossary is used',
        correction: 'AI correction',
        correctionText: 'The AI fixes the spelling of the terms, helped by their pronunciation.',
        translation: 'AI translation',
        translationText: 'Terms are kept as they are, or translated as you specified.',
        transcription: 'Transcription',
        transcriptionOn: 'The terms are given to the transcription model so it recognizes them.',
        transcriptionOff: "The transcription does not use the glossary: it relies on the prompt in the sync options.",
      },
      form: {
        term: 'Term',
        termPlaceholder: 'E.g. Videas, Mediasbox, NATO',
        termHint: 'Written exactly as it should appear in the subtitles.',
        phoneticHint: 'Pronunciation',
        phoneticHintPlaceholder: 'E.g. vee-day-as',
        phoneticHintHint: 'How the word sounds: helps the AI find it in a badly transcribed text.',
        context: 'Note',
        contextPlaceholder: 'E.g. product name, always capitalized',
        contextHint: 'For your team only: the note is not sent to the AI.',
        category: 'Category',
        categoryPlaceholder: 'E.g. Brand, Person, Place',
        categoryHint: 'To organize and filter your terms.',
        isActive: 'Active',
        isActiveHint: 'An inactive term stays in the list but is no longer used.',
        required: 'This field is required',
        duplicate: 'This term already exists for this language.',
        cancel: 'Cancel',
        add: 'Add',
        save: 'Save',
        delete: 'Delete',
      },
      delete: {
        title: 'Delete term',
        message: 'Are you sure you want to delete the term "{term}" from the glossary? This action cannot be undone.',
        success: 'Term deleted',
        error: 'The term could not be deleted.',
      },
      import: {
        button: 'Import',
        title: 'Import glossary terms',
        description: 'Import terms from a CSV file to quickly populate your glossary.',
        formatTip: 'Your CSV file must have column headers in the first row. Use comma or semicolon as separator. UTF-8 encoding is recommended.',
        requiredColumn: 'Required column: {column}',
        optionalColumns: 'Optional columns: {columns}',
        downloadTemplate: 'Download template',
        replaceExisting: 'Replace all existing terms',
        replaceExistingHint: 'All current glossary terms will be deleted before importing.',
        selectFile: 'Select a file',
        changeFile: 'Change file',
        termsFound: '{count} term found | {count} terms found',
        termsSkipped: '{count} row skipped (missing term) | {count} rows skipped (missing term)',
        importButton: 'Import {count} term | Import {count} terms',
        importing: 'Importing...',
        success: 'Import complete: {created} created, {updated} updated, {skipped} skipped.',
        error: 'Import failed. Please check your file and try again.',
        emptyFile: 'The file contains no data rows.',
        noTermColumn: 'No "term" column found. Please ensure your file has a header named "term" or "terme".',
        parseError: 'Failed to parse file. Please check the format.',
      },
    },
    vocabulary: {
      title: 'Custom Vocabulary',
      description: 'Add specific terms to improve their recognition during automatic transcription.',
      add: 'Add',
      addTerm: 'Add term',
      editTerm: 'Edit term',
      termPlaceholder: 'Add a new term...',
      searchPlaceholder: 'Search for a term...',
      noTerms: 'No custom terms',
      noTermsHint: 'Add terms to improve recognition during transcription',
      noResults: 'No results',
      noResultsHint: 'No terms match your search',
      saved: 'Vocabulary saved successfully',
      form: {
        term: 'Term to recognize',
        termPlaceholder: 'Ex: Videas, COVID-19, SaaS, REST API',
        hint: 'This term will be used to improve recognition during automatic transcription.',
        required: 'This field is required',
        cancel: 'Cancel',
        add: 'Add',
        save: 'Save',
      },
    },
    sync: {
      title: 'Sync options',
      description: 'How your videos are transcribed and how the text is cut into subtitles. Changes apply to the next transcriptions, corrections and translations; existing subtitles do not move.',
      saved: 'Options saved',
      saveError: 'The options were not saved.',
      sections: {
        transcription: 'Transcription',
        format: 'Lines',
        timing: 'Rhythm',
      },
      transcriptionLead: 'The prompt gives the model the context of your videos: topic, proper names, acronyms. It is read at the start of every transcription.',
      initialPrompt: 'Transcription prompt',
      initialPromptPlaceholder: 'E.g. County council session, with the chair, the rapporteurs and the councillors.',
      promptHint: 'Write sentences rather than a list of words. To enforce the spelling of a term in AI corrections, use the glossary.',
      promptWithGlossary: 'Write sentences rather than a list of words. The active glossary terms are added before the prompt.',
      toGlossary: 'Open the glossary',
      formatLead: 'Applied to AI corrections and translations: a subtitle that is too long is cut into several.',
      formatLeadShaping: 'Applied to every new transcription and to AI corrections and translations: each subtitle fits on this many lines.',
      maxLines: 'Lines per subtitle',
      maxCharsPerLine: 'Characters per line',
      maxCharsHint: 'Between 32 and 42 is the subtitling standard.',
      sample: 'Example',
      sampleText: "Welcome to this session of the county council. We will review the reports on today's agenda, starting with the budget for secondary schools and school transport.",
      timingLead: "Applied to every new transcription: subtitles read too fast are cut or extended, using each word's timing.",
      maxCharsPerSecond: 'Reading speed (characters per second)',
      cpsHint: 'Above it, a subtitle is cut or stays on screen longer. 17 to 20 suits most audiences.',
      minDuration: 'Minimum duration',
      minDurationHint: 'A subtitle is not cut before it has lasted at least this long.',
      maxDuration: 'Maximum duration',
      maxDurationHint: 'A longer subtitle is cut; a subtitle read too fast is not extended past it.',
      slow: 'Slow',
      comfortable: 'Comfortable',
      fast: 'Fast',
      resetDefaults: 'Restore defaults',
      discard: 'Discard changes',
      save: 'Save',
    },
    style: {
      title: 'Default style',
      description: 'The style of the subtitles burned into videos exported from Scribe, for the whole organization. A video you give its own style in the editor keeps it.',
      saved: 'Default style saved',
      saveError: 'Error saving the style',
      loadError: 'Error loading the style',
      sections: {
        themes: 'Start from a theme',
        text: 'Text',
        background: 'Background',
        stroke: 'Outline',
        position: 'Position',
      },
      themesHint: 'A theme fills in the settings; adjust them if needed, then save.',
      allThemes: 'See all {count} themes',
      fewerThemes: 'See fewer themes',
      fontFamily: 'Font',
      fontSize: 'Size',
      weightRegular: 'Regular',
      weightBold: 'Bold',
      italic: 'Italic',
      uppercase: 'Uppercase',
      textColor: 'Text color',
      backgroundColor: 'Background color',
      backgroundOpacity: 'Background opacity',
      backgroundHint: 'The background is a box behind each line. At 0 %, there is no box and the text keeps its outline.',
      strokeWidth: 'Width',
      strokeColor: 'Outline color',
      strokeBoxedHint: 'No effect while the background shows: the export draws either a box or an outline. Set the background opacity to 0 % to use the outline.',
      verticalPosition: 'Height',
      verticalPositionHint: "Distance above players' control bar, as a percentage of the video's height.",
      preview: 'Preview',
      previewFaithful: 'Faithful to the exported video',
      showPlayerBar: 'Player bar',
      previewText: 'Your subtitle will look like this\non the exported video.',
      resetDefaults: 'Restore the Videas style',
      discard: 'Discard changes',
      save: 'Save',
    },
  },
  processing: {
    title: 'Processing',
    openEditor: 'Open Editor',
    uploading: 'Uploading... {progress}%',
    canCloseHint: 'You can close this window. Processing will continue in the background.',
    status: {
      creating: 'Creating subtitles...',
      pending: 'Preparing...',
      uploading: 'Uploading file...',
      processing: 'Generating subtitles...',
      ready: 'Subtitles ready!',
      error: 'An error occurred',
    },
    description: {
      library: 'Generating subtitles for your video...',
      upload: 'Uploading your file and generating subtitles...',
      srt: 'Importing your subtitle file...',
      ready: 'Your subtitles have been generated successfully.',
      error: 'Something went wrong during processing.',
    },
    languageStatus: {
      pending: 'Pending',
      processing: 'Processing',
      ready: 'Ready',
      error: 'Failed',
    },
    error: {
      missingData: 'Missing data for processing',
      noAsset: 'No asset selected',
      noFile: 'No file selected',
      noWorkspace: 'You need a workspace to store the generated video. Create one to continue.',
      noOrganization: 'No organization selected',
      uploadFailed: 'Upload failed',
      generation: 'Failed to generate subtitles',
      credits: 'Not enough credits: nothing was started.',
      costDeclined: 'Generation cancelled: nothing was started.',
      import: 'Failed to import subtitle file',
      noSegments: 'No subtitles found in the file',
      allFailed: 'All subtitle generations failed',
      generic: 'An error occurred while creating subtitles',
    },
  },
  wizard: {
    title: 'Create Subtitles',
    source: {
      title: 'Source',
      subtitle: 'Choose how you want to add your content',
      srt: {
        title: 'Import subtitles',
        description: 'Upload an SRT or WebVTT file',
      },
      upload: {
        title: 'Upload video/audio',
        description: 'Upload a video or audio file to transcribe',
      },
      library: {
        title: 'From library',
        description: 'Select an existing asset from your library',
      },
    },
    file: {
      title: 'File',
      destination: 'Destination',
      workspace: 'Workspace',
      folder: 'Folder (optional)',
      folderPlaceholder: 'Search folder...',
      uploadSrt: 'Upload subtitle file',
      uploadMedia: 'Upload video or audio',
      srtFormats: 'Supported formats: SRT, VTT, WebVTT (max 10 MB)',
      mediaFormats: 'Supported formats: MP4, MOV, AVI, MP3, WAV...',
      validating: 'Validating...',
      validationFailed: 'Validation failed',
      cues: 'cues',
      words: 'words',
      language: 'Language',
      selectAsset: 'Select a video or audio',
      targetAsset: 'Target video or audio',
      targetAssetOptional: 'Link to a video or audio (optional)',
      selectTargetAsset: 'Select a video or audio',
      selectTargetHint: 'Choose the file to attach subtitles to',
      selectTargetOptionalHint: 'Or skip to open the editor without media',
      selectFromLibrary: 'Select a file from your library',
      browseLibrary: 'Browse your library',
      browseHint: 'Click to open the file selector',
    },
    languages: {
      title: 'Languages',
      subtitle: 'Select the languages for subtitle generation',
      selected: 'Selected',
      addLanguages: 'Add languages',
      searchPlaceholder: 'Search all languages...',
      noResults: 'No languages found',
      popular: 'Popular languages',
      multipleSelected: '{count} languages selected',
    },
    validation: {
      selectSource: 'Please select a source type',
      selectFile: 'Please select or upload a file',
      selectAsset: 'Please select a target video or audio',
      selectLanguage: 'Please select at least one language',
      waitValidation: 'Please wait for file validation',
    },
  },
}
